import { useState } from "react";
import { Plus, Save, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  useQuestionCategories, useCreateQuestionCategory, useUpdateQuestionCategory, useDeleteQuestionCategory,
  useQuestions, useCreateQuestion,
} from "@/hooks/api";
import type { QuestionCategory } from "@/lib/api_client";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { QuestionCard } from "./question-card";

export function QuestionsPage() {
  return (
    <div className="space-y-4">
      <Tabs defaultValue="questions">
        <TabsList>
          <TabsTrigger value="questions">Sporsmal</TabsTrigger>
          <TabsTrigger value="categories">Kategorier</TabsTrigger>
        </TabsList>
        <TabsContent value="questions"><QuestionsTab /></TabsContent>
        <TabsContent value="categories"><QuestionCategoriesTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function QuestionsTab() {
  const { data: categories, isLoading: catsLoading } = useQuestionCategories();
  const { data: questions, isLoading: qLoading } = useQuestions();
  const createQuestion = useCreateQuestion();

  const sortedCategories = (categories ?? []).slice().sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0));

  const handleAddQuestion = async (categoryId: string) => {
    const catQuestions = (questions ?? []).filter((q) => q.categoryId === categoryId);
    await createQuestion.mutateAsync({
      categoryId,
      question: "Nytt sporsmal",
      description: "",
      nickname: "",
      kind: "onboarding",
      tags: "",
      advancedQuestionType: null,
      sequenceNumber: catQuestions.length + 1,
      isNumber: false,
      multipleSelect: false,
    });
    toast.success("Sporsmal opprettet");
  };

  if (catsLoading || qLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-admin-text-subtle" /></div>;
  }

  return (
    <div className="space-y-6">
      {sortedCategories.map((cat) => {
        const catQuestions = (questions ?? [])
          .filter((q) => q.categoryId === cat.id)
          .sort((a, b) => Number(a.sequenceNumber ?? 0) - Number(b.sequenceNumber ?? 0));
        return (
          <div key={cat.id} className="space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-sm">{cat.name}</h3>
                <p className="text-xs text-admin-text-subtle">{cat.description}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => handleAddQuestion(cat.id)} className="h-7 text-xs" disabled={createQuestion.isPending}>
                <Plus className="h-3 w-3" />
                Nytt sporsmal
              </Button>
            </div>
            <div className="space-y-2">
              {catQuestions.map((q) => (
                <QuestionCard key={q.id} question={q} />
              ))}
              {catQuestions.length === 0 && (
                <p className="text-sm text-admin-text-subtle pl-2">Ingen sporsmal i denne kategorien</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function QuestionCategoriesTab() {
  const { data: categories, isLoading } = useQuestionCategories();
  const createQC = useCreateQuestionCategory();
  const updateQC = useUpdateQuestionCategory();
  const deleteQC = useDeleteQuestionCategory();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<{ name: string; description: string; introHeader: string; order: string } | null>(null);

  const handleEdit = (cat: QuestionCategory) => {
    setEditingId(cat.id);
    setForm({ name: cat.name, description: cat.description ?? "", introHeader: cat.introHeader ?? "", order: cat.order ?? "0" });
  };

  const handleCancel = () => { setEditingId(null); setForm(null); };

  const handleSave = async (id: string) => {
    if (!form) return;
    await updateQC.mutateAsync({ id, data: form });
    handleCancel();
    toast.success("Kategori lagret");
  };

  const handleCreate = async () => {
    const cat = await createQC.mutateAsync({
      name: "Ny kategori",
      description: "",
      introHeader: "",
      order: String((categories ?? []).length + 1),
    });
    handleEdit(cat);
  };

  const handleDelete = async (id: string) => {
    await deleteQC.mutateAsync(id);
    if (editingId === id) handleCancel();
    toast.success("Kategori slettet");
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-admin-text-subtle" /></div>;
  }

  const sorted = (categories ?? []).slice().sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0));

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={handleCreate} size="sm" disabled={createQC.isPending}>
          <Plus className="h-4 w-4" />
          Ny kategori
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {sorted.map((cat) => {
          const isEditing = editingId === cat.id;
          return (
            <Card key={cat.id}>
              <CardContent className="p-4 space-y-3">
                {isEditing && form ? (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-admin-text-subtle mb-1">Navn</label>
                      <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-admin-text-subtle mb-1">Beskrivelse</label>
                      <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="min-h-[60px]" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-admin-text-subtle mb-1">Intro-tekst</label>
                      <Textarea value={form.introHeader} onChange={(e) => setForm({ ...form, introHeader: e.target.value })} className="min-h-[60px]" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-admin-text-subtle mb-1">Rekkefolge</label>
                      <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="w-20" />
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" onClick={() => handleSave(cat.id)} disabled={updateQC.isPending}>
                        <Save className="h-3 w-3" />
                        Lagre
                      </Button>
                      <Button size="sm" variant="ghost" onClick={handleCancel}>Avbryt</Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold">{cat.name}</h3>
                        <p className="text-xs text-admin-text-subtle">Rekkefolge: {cat.order}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(cat)} className="text-xs h-7">Rediger</Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(cat.id)} className="text-admin-negative hover:text-admin-negative h-7 w-7">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-admin-text-secondary">{cat.description}</p>
                    {cat.introHeader && (
                      <p className="text-xs text-admin-text-subtle italic">"{cat.introHeader}"</p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
